/**
 * WAF edge analytics (Owl) — the Digital Experience Monitoring diagram. Edge
 * request volume per platform for a single domain (subdomain FQDN).
 * Service-scoped, `GET /services/:service_id/waf/analytics/edge-query`.
 */
import { useQuery } from '@tanstack/react-query';
import { serviceApiV3, wafKeys, unwrap } from './_shared';

/**
 * Edge-query diagram: shared x-axis (`labels`, unix ms) + per-platform datasets.
 * Each dataset's `series` maps a metric (e.g. `edge_requests`) to per-bucket
 * values (`null` = gap). `label` is a platform id.
 */
export interface WafEdgeQueryDiagram {
  labels: number[];
  datasets: {
    label: string;
    type?: string;
    series: Record<string, (number | null)[]>;
  }[];
}

export interface WafEdgeQueryFilter {
  /** Domain name (the subdomain FQDN). */
  domain: string;
  /** Start unix seconds. */
  start_time: number;
  /** End unix seconds. */
  end_time: number;
  /** Bucket seconds (default 86400). */
  interval?: number;
}

/** Edge-query diagram for one domain. */
export function useWafEdgeQueryDiagram(
  serviceId: string | undefined,
  filter: WafEdgeQueryFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafEdgeQueryDiagram>({
    queryKey: wafKeys.edgeQueryDiagram(serviceId ?? '', params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafEdgeQueryDiagram>(
        await serviceApiV3.get(`/services/${serviceId}/waf/analytics/edge-query`, { params }),
      ),
  });
}

/**
 * Origin Shield diagram — a flat single series (`labels` unix ms + `data`). The
 * `traffic` kind returns bytes per bucket, `request` returns request counts.
 * No total in the response → sum `data` client-side.
 */
export interface WafOriginShieldDiagram {
  labels: number[];
  data: number[];
}

export type WafOriginShieldKind = 'traffic' | 'request';

export interface WafOriginShieldFilter {
  /** Domain name (the subdomain FQDN). */
  domain: string;
  /** Start unix seconds. */
  start_time: number;
  /** End unix seconds. */
  end_time: number;
  /** Bucket seconds (0/omitted = BE auto-pick). */
  interval?: number;
}

/** Origin Shield traffic/request diagram for one domain. */
export function useWafOriginShieldDiagram(
  serviceId: string | undefined,
  kind: WafOriginShieldKind,
  filter: WafOriginShieldFilter,
  enabled = true,
) {
  const params = { ...filter };
  return useQuery<WafOriginShieldDiagram>({
    queryKey: wafKeys.originShieldDiagram(serviceId ?? '', kind, params as Record<string, unknown>),
    enabled: !!serviceId && !!filter.domain && enabled,
    staleTime: 30_000,
    queryFn: async () =>
      unwrap<WafOriginShieldDiagram>(
        await serviceApiV3.get(`/services/${serviceId}/waf/analytics/origin-shield/${kind}`, { params }),
      ),
  });
}
